import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  LinearProgress,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import { default as GridLegacy } from '@mui/material/Grid';
import {
  Phone as PhoneIcon,
  Contacts as ContactsIcon,
  AccessTime as TimeIcon,
  Speed as SpeedIcon,
  TrendingUp as SalesIcon,
  ShowChart as SphIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { format, subDays } from 'date-fns';

// API Service - Inline definition (same as TeamScorecard)
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5010';

const salesApi = {
  getTeamScorecard: async (customDay: string | null = null, period: string = 'currentWeek') => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const dayParam = customDay ? `&day=${customDay}` : '';
    const periodParam = period ? `&period=${period}` : '';
    
    const response = await fetch(
      `${API_BASE_URL}/api/scorecard/team?userId=${user.id}${dayParam}${periodParam}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    
    if (!response.ok) {
      throw new Error('Failed to fetch scorecard');
    }
    
    return response.json();
  },
};

// Type definitions (same as TeamScorecard)
interface User {
  id: number;
  username: string;
  email: string;
  role: string;
}

interface MetricData {
  actual: number;
  goal: number;
  color: 'green' | 'yellow' | 'red' | 'grey';
  actualFormatted?: string;
  goalFormatted?: string;
}

interface PeriodData {
  calls?: MetricData;
  contacts?: MetricData;
  loginTime?: MetricData;
  utilization?: MetricData;
  sales?: MetricData;
  sph?: MetricData;
}

interface ScorecardItem {
  userId: number;
  username: string;
  attributionId: string;
  yesterday?: PeriodData;
  currentWeek?: PeriodData;
}

interface TeamScorecardResponse {
  scorecards: ScorecardItem[];
}

type PeriodType = 'currentWeek' | 'previousWeek' | 'currentMonth' | 'previousMonth';
type ColorCode = 'green' | 'yellow' | 'red' | 'grey';

interface ColorConfig {
  main: string;
  light: string;
  text: string;
}

interface MetricCardProps {
  title: string;
  icon: React.ReactElement;
  metric: MetricData;
  period: string;
}

const PersonalScorecard: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [selectedDay, setSelectedDay] = useState<string>(
    format(subDays(new Date(), 1), 'yyyy-MM-dd')
  );
  const [period, setPeriod] = useState<PeriodType>('currentWeek');
  const [tabValue, setTabValue] = useState<number>(0); // 0 = Yesterday, 1 = Current Week

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  // Use SAME endpoint as TeamScorecard - filter for current user
  const {
    data: scorecardData,
    isLoading,
    error,
  } = useQuery<TeamScorecardResponse>({
    queryKey: tabValue === 0 
      ? ['personalScorecard', 'daily', selectedDay]
      : ['personalScorecard', 'period', period],
    queryFn: () => salesApi.getTeamScorecard(
      tabValue === 0 ? selectedDay : null,
      period
    ),
    refetchInterval: 5 * 60 * 1000,
    enabled: !!user,
  });

  const handleDayChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = event.target.value;
    setSelectedDay(newDate || format(subDays(new Date(), 1), 'yyyy-MM-dd'));
    setTabValue(0); // Switch to daily view
  };

  const handlePeriodChange = (event: SelectChangeEvent<PeriodType>) => {
    setPeriod(event.target.value as PeriodType);
    setTabValue(1); // Switch to period view
  };

  // Filter for ONLY the logged-in user
  const myScorecard = scorecardData?.scorecards?.find(s => s.userId === user?.id);

  const getColor = (colorCode: ColorCode): ColorConfig => {
    switch (colorCode) {
      case 'green':
        return { main: '#4caf50', light: '#e8f5e9', text: '#2e7d32' };
      case 'yellow':
        return { main: '#ffa726', light: '#fff3e0', text: '#ef6c00' };
      case 'red':
        return { main: '#f44336', light: '#ffebee', text: '#c62828' };
      case 'grey':
      default:
        return { main: '#9e9e9e', light: '#f5f5f5', text: '#616161' };
    }
  };

  const MetricCard: React.FC<MetricCardProps> = ({ title, icon, metric, period }) => {
    const colors = getColor(metric.color);
    const isNoData = metric.color === 'grey';

    return (
      <Card
        sx={{
          height: '100%',
          borderLeft: `4px solid ${colors.main}`,
          backgroundColor: colors.light,
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4,
          },
        }}
      >
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <Box
              sx={{
                backgroundColor: colors.main,
                color: 'white',
                borderRadius: '50%',
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
              }}
            >
              {icon}
            </Box>
            <Typography variant="h6" color="textPrimary" fontWeight="bold">
              {title}
            </Typography>
          </Box>

          {isNoData ? (
            <Box textAlign="center" py={2}>
              <Typography variant="body1" color="textSecondary">
                No data available
              </Typography>
              <Typography variant="caption" color="textSecondary">
                for {period}
              </Typography>
            </Box>
          ) : (
            <>
              <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={1}>
                <Typography variant="h4" fontWeight="bold" color={colors.text}>
                  {metric.actualFormatted || metric.actual}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  / {metric.goalFormatted || metric.goal}
                </Typography>
              </Box>

              <LinearProgress
                variant="determinate"
                value={Math.min((metric.actual / metric.goal) * 100, 100)}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: colors.light,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: colors.main,
                    borderRadius: 4,
                  },
                }}
              />

              <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                <Typography variant="body2" fontWeight="bold" color={colors.text}>
                  {Math.round((metric.actual / metric.goal) * 100)}% Complete
                </Typography>
                <Chip
                  label={
                    metric.color === 'green'
                      ? 'Compliant'
                      : metric.color === 'yellow'
                      ? '90-99%'
                      : 'Below Target'
                  }
                  size="small"
                  sx={{
                    backgroundColor: colors.main,
                    color: 'white',
                    fontWeight: 'bold',
                  }}
                />
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Failed to load your scorecard. Please check your connection and try again.
        </Alert>
      </Container>
    );
  }

  if (!myScorecard) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="info">
          No scorecard data available. Please contact your administrator.
        </Alert>
      </Container>
    );
  }

  const displayMetrics = tabValue === 0 ? myScorecard.yesterday : myScorecard.currentWeek;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" gutterBottom>
          Welcome, {user?.username || 'User'}!
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Your Personal Performance Scorecard
        </Typography>
      </Box>

      {/* Daily Performance - Previous Day */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          flexWrap="wrap"
          gap={2}
        >
          <Typography variant="h5" fontWeight="bold">
            Daily Performance - Previous Day
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="body2" color="textSecondary">
              Select Day:
            </Typography>
            <TextField
              type="date"
              size="small"
              value={selectedDay || format(subDays(new Date(), 1), 'yyyy-MM-dd')}
              onChange={handleDayChange}
              sx={{ minWidth: 180 }}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                max: format(new Date(), 'yyyy-MM-dd'),
              }}
            />
            <Chip
              label={format(new Date(selectedDay + 'T00:00:00'), 'MMMM dd, yyyy')}
              color="primary"
              variant="outlined"
            />
          </Box>
        </Box>

        <GridLegacy container spacing={3}>
          <GridLegacy size={{ xs: 12, sm: 6, md: 4 }}>
            <MetricCard
              title="Calls"
              icon={<PhoneIcon />}
              metric={myScorecard.yesterday?.calls || { actual: 0, goal: 0, color: 'grey' }}
              period="selected day"
            />
          </GridLegacy>
          <GridLegacy size={{ xs: 12, sm: 6, md: 4 }}>
            <MetricCard
              title="Contacts"
              icon={<ContactsIcon />}
              metric={myScorecard.yesterday?.contacts || { actual: 0, goal: 0, color: 'grey' }}
              period="selected day"
            />
          </GridLegacy>
          <GridLegacy size={{ xs: 12, sm: 6, md: 4 }}>
            <MetricCard
              title="Login Time"
              icon={<TimeIcon />}
              metric={myScorecard.yesterday?.loginTime || { actual: 0, goal: 0, color: 'grey' }}
              period="selected day"
            />
          </GridLegacy>
          <GridLegacy size={{ xs: 12, sm: 6, md: 4 }}>
            <MetricCard
              title="Utilization"
              icon={<SpeedIcon />}
              metric={myScorecard.yesterday?.utilization || { actual: 0, goal: 0, color: 'grey' }}
              period="selected day"
            />
          </GridLegacy>
          <GridLegacy size={{ xs: 12, sm: 6, md: 4 }}>
            <MetricCard
              title="Sales"
              icon={<SalesIcon />}
              metric={myScorecard.yesterday?.sales || { actual: 0, goal: 0, color: 'grey' }}
              period="selected day"
            />
          </GridLegacy>
          <GridLegacy size={{ xs: 12, sm: 6, md: 4 }}>
            <MetricCard
              title="SPH"
              icon={<SphIcon />}
              metric={myScorecard.yesterday?.sph || { actual: 0, goal: 0, color: 'grey' }}
              period="selected day"
            />
          </GridLegacy>
        </GridLegacy>
      </Paper>

      {/* Period Progress */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          flexWrap="wrap"
          gap={2}
        >
          <Typography variant="h5" fontWeight="bold">
            Period Performance
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Period</InputLabel>
              <Select value={period} label="Period" onChange={handlePeriodChange}>
                <MenuItem value="currentWeek">Current Week</MenuItem>
                <MenuItem value="previousWeek">Previous Week</MenuItem>
                <MenuItem value="currentMonth">Current Month</MenuItem>
                <MenuItem value="previousMonth">Previous Month</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <GridLegacy container spacing={3}>
          <GridLegacy size={{ xs: 12, sm: 6, md: 4 }}>
            <MetricCard
              title="Calls"
              icon={<PhoneIcon />}
              metric={myScorecard.currentWeek?.calls || { actual: 0, goal: 0, color: 'grey' }}
              period="this period"
            />
          </GridLegacy>
          <GridLegacy size={{ xs: 12, sm: 6, md: 4 }}>
            <MetricCard
              title="Contacts"
              icon={<ContactsIcon />}
              metric={myScorecard.currentWeek?.contacts || { actual: 0, goal: 0, color: 'grey' }}
              period="this period"
            />
          </GridLegacy>
          <GridLegacy size={{ xs: 12, sm: 6, md: 4 }}>
            <MetricCard
              title="Login Time"
              icon={<TimeIcon />}
              metric={myScorecard.currentWeek?.loginTime || { actual: 0, goal: 0, color: 'grey' }}
              period="this period"
            />
          </GridLegacy>
          <GridLegacy size={{ xs: 12, sm: 6, md: 4 }}>
            <MetricCard
              title="Utilization"
              icon={<SpeedIcon />}
              metric={myScorecard.currentWeek?.utilization || { actual: 0, goal: 0, color: 'grey' }}
              period="this period"
            />
          </GridLegacy>
          <GridLegacy size={{ xs: 12, sm: 6, md: 4 }}>
            <MetricCard
              title="Sales"
              icon={<SalesIcon />}
              metric={myScorecard.currentWeek?.sales || { actual: 0, goal: 0, color: 'grey' }}
              period="this period"
            />
          </GridLegacy>
          <GridLegacy size={{ xs: 12, sm: 6, md: 4 }}>
            <MetricCard
              title="SPH"
              icon={<SphIcon />}
              metric={myScorecard.currentWeek?.sph || { actual: 0, goal: 0, color: 'grey' }}
              period="this period"
            />
          </GridLegacy>
        </GridLegacy>
      </Paper>

      {/* Legend */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Compliance Legend
        </Typography>
        <Box display="flex" gap={3} flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#4caf50', borderRadius: 1 }} />
            <Typography variant="body2">100% Compliant (Green)</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#ffa726', borderRadius: 1 }} />
            <Typography variant="body2">90-99.9% Compliant (Yellow)</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#f44336', borderRadius: 1 }} />
            <Typography variant="body2">Below 90% (Red)</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ width: 20, height: 20, backgroundColor: '#9e9e9e', borderRadius: 1 }} />
            <Typography variant="body2">No Data (Grey)</Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default PersonalScorecard;
